import { EMPTY_VALUE } from "@/billing/constants/empty-value";
import type { PracticeLedgerDateRange, PracticeLedgerRow } from "@/billing/types/practice-ledger";
import { formatUtcCalendarDate } from "@/billing/utils/practice-ledger-date-range";
import { neutralizeCsvFormulaPrefix, rowsToCsv } from "@/shared/utils/csv";
import { usDateFormat } from "@/utils/datetime";

interface PracticeLedgerCsvColumn {
  header: string;
  value: (row: PracticeLedgerRow) => string;
}

/**
 * Ordered CSV columns. Client name and MRN are split so the file matches the table without
 * nesting those values in one cell.
 */
export const PRACTICE_LEDGER_CSV_COLUMNS: PracticeLedgerCsvColumn[] = [
  { header: "Client Name", value: (row) => neutralizeCsvFormulaPrefix(row.clientName) },
  {
    header: "Medical Record #",
    value: (row) => neutralizeCsvFormulaPrefix(row.medicalRecordNumber),
  },
  { header: "Type", value: (row) => row.type },
  {
    header: "Transaction Date",
    value: (row) => neutralizeCsvFormulaPrefix(usDateFormat(row.transactionDate) || EMPTY_VALUE),
  },
  { header: "Source", value: (row) => neutralizeCsvFormulaPrefix(row.sourceLabel) },
  { header: "Amount", value: (row) => row.amount.toFixed(2) },
  {
    header: "#ID",
    value: (row) => neutralizeCsvFormulaPrefix(row.statementId ?? EMPTY_VALUE),
  },
];

/** Window-scoped filename with no client identifiers. */
export function getPracticeLedgerExportFilename(dateRange: PracticeLedgerDateRange): string {
  const start = formatUtcCalendarDate(dateRange.startDate);
  const end = formatUtcCalendarDate(dateRange.endDate);
  return `practice-ledger-${start}-to-${end}.csv`;
}

/**
 * Serializes ledger rows to CSV. Header and data rows share {@link PRACTICE_LEDGER_CSV_COLUMNS}.
 */
export function buildPracticeLedgerCsv(rows: PracticeLedgerRow[]): string {
  return rowsToCsv([
    PRACTICE_LEDGER_CSV_COLUMNS.map((column) => column.header),
    ...rows.map((row) => PRACTICE_LEDGER_CSV_COLUMNS.map((column) => column.value(row))),
  ]);
}
